import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Settings,
  Package,
  UtensilsCrossed,
  Users,
  Calculator,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  BookOpen
} from 'lucide-react';

interface TutorialStepProps {
  stepNumber: number;
  title: string;
  description: string;
  details: string[];
  imageSrc: string;
  imageAlt: string;
  icon: React.ReactNode;
}

function TutorialStep({ stepNumber, title, description, details, imageSrc, imageAlt, icon }: TutorialStepProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-primary/5">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
            {stepNumber}
          </div>
          <div className="flex items-center gap-2">
            {icon}
            <CardTitle className="text-xl">{title}</CardTitle>
          </div>
        </div>
        <CardDescription className="text-base mt-2">{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <ul className="space-y-3">
              {details.map((detail, index) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle2 className={`h-5 w-5 text-green-500 mt-0.5 shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg overflow-hidden border shadow-sm">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Tutorial() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const tutorialSteps: Omit<TutorialStepProps, 'stepNumber'>[] = [
    {
      title: t('tutorial.steps.settings.title'),
      description: t('tutorial.steps.settings.description'),
      details: [
        t('tutorial.steps.settings.detail1'),
        t('tutorial.steps.settings.detail2'),
        t('tutorial.steps.settings.detail3'),
        t('tutorial.steps.settings.detail4'),
      ],
      imageSrc: '/tutorial/03-settings.png',
      imageAlt: 'Settings page screenshot',
      icon: <Settings className="h-6 w-6 text-primary" />,
    },
    {
      title: t('tutorial.steps.inventory.title'),
      description: t('tutorial.steps.inventory.description'),
      details: [
        t('tutorial.steps.inventory.detail1'),
        t('tutorial.steps.inventory.detail2'),
        t('tutorial.steps.inventory.detail3'),
        t('tutorial.steps.inventory.detail4'),
      ],
      imageSrc: '/tutorial/04-inventory.png',
      imageAlt: 'Inventory page screenshot',
      icon: <Package className="h-6 w-6 text-primary" />,
    },
    {
      title: t('tutorial.steps.menu.title'),
      description: t('tutorial.steps.menu.description'),
      details: [
        t('tutorial.steps.menu.detail1'),
        t('tutorial.steps.menu.detail2'),
        t('tutorial.steps.menu.detail3'),
        t('tutorial.steps.menu.detail4'),
      ],
      imageSrc: '/tutorial/05-menu.png',
      imageAlt: 'Menu page screenshot',
      icon: <UtensilsCrossed className="h-6 w-6 text-primary" />,
    },
    {
      title: t('tutorial.steps.staff.title'),
      description: t('tutorial.steps.staff.description'),
      details: [
        t('tutorial.steps.staff.detail1'),
        t('tutorial.steps.staff.detail2'),
        t('tutorial.steps.staff.detail3'),
        t('tutorial.steps.staff.detail4'),
      ],
      imageSrc: '/tutorial/06-staff.png',
      imageAlt: 'Staff page screenshot',
      icon: <Users className="h-6 w-6 text-primary" />,
    },
    {
      title: t('tutorial.steps.accounting.title'),
      description: t('tutorial.steps.accounting.description'),
      details: [
        t('tutorial.steps.accounting.detail1'),
        t('tutorial.steps.accounting.detail2'),
        t('tutorial.steps.accounting.detail3'),
        t('tutorial.steps.accounting.detail4'),
      ],
      imageSrc: '/tutorial/07-accounting.png',
      imageAlt: 'Accounting page screenshot',
      icon: <Calculator className="h-6 w-6 text-primary" />,
    },
    {
      title: t('tutorial.steps.cancellations.title'),
      description: t('tutorial.steps.cancellations.description'),
      details: [
        t('tutorial.steps.cancellations.detail1'),
        t('tutorial.steps.cancellations.detail2'),
        t('tutorial.steps.cancellations.detail3'),
        t('tutorial.steps.cancellations.detail4'),
      ],
      imageSrc: '/tutorial/08-cancellations.png',
      imageAlt: 'Cancellations page screenshot',
      icon: <AlertCircle className="h-6 w-6 text-primary" />,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold">{t('tutorial.title')}</h1>
          </div>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('tutorial.subtitle')}
          </p>
        </div>

        {/* Overview Card */}
        <Card className="mb-8 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              {t('tutorial.overview.title')}
            </CardTitle>
            <CardDescription>
              {t('tutorial.overview.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Settings className="h-5 w-5 text-primary" />
                <span className="font-medium">1. {t('tutorial.steps.settings.title')}</span>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Package className="h-5 w-5 text-primary" />
                <span className="font-medium">2. {t('tutorial.steps.inventory.title')}</span>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <UtensilsCrossed className="h-5 w-5 text-primary" />
                <span className="font-medium">3. {t('tutorial.steps.menu.title')}</span>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-medium">4. {t('tutorial.steps.staff.title')}</span>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Calculator className="h-5 w-5 text-primary" />
                <span className="font-medium">5. {t('tutorial.steps.accounting.title')}</span>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <AlertCircle className="h-5 w-5 text-primary" />
                <span className="font-medium">6. {t('tutorial.steps.cancellations.title')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tutorial Steps */}
        <div className="space-y-8">
          {tutorialSteps.map((step, index) => (
            <TutorialStep
              key={index}
              stepNumber={index + 1}
              {...step}
            />
          ))}
        </div>

        {/* CTA Section */}
        <Card className="mt-12 bg-primary text-primary-foreground">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">{t('tutorial.cta.title')}</h2>
            <p className="mb-6 opacity-90">{t('tutorial.cta.description')}</p>
            <Link to="/">
              <Button variant="secondary" size="lg" className="gap-2">
                {t('tutorial.cta.button')}
                <ArrowIcon className="h-5 w-5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
